import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Link,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SchoolIcon from '@mui/icons-material/School';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAuth } from '../context/AuthContext';
import { useAppTheme } from '../context/ThemeContext';
import everguardLogo from '../assets/EverGuard_Logo.svg';

const DRAWER_FULL = 240;
const DRAWER_MINI = 64;

const MENU_ITEMS = [
  { label: 'Profile', icon: <AccountCircleIcon />, path: '/app/profile' },
  { label: 'Training', icon: <SchoolIcon />, path: '/app/training' },
];

const AppLayout = () => {

  const { user, logout } = useAuth();
  const { mode, toggleMode } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((s) => !s);
    } else {
      setOpen((s) => !s);
    }
  };

  const isActive = (path) => location.pathname.startsWith(path);
  const drawerWidth = open ? DRAWER_FULL : DRAWER_MINI;

  const DrawerContent = ({ inMobile = false }) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Logo — always at the very top */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open || inMobile ? 'flex-start' : 'center',
          px: open || inMobile ? 2 : 1,
          py: 1.5,
          minHeight: 64,
          borderBottom: '1px solid rgba(0,0,0,0.07)',
          flexShrink: 0,
        }}
      >
        <img
          src={everguardLogo}
          alt="EverGuard"
          style={{
            height: 42,
            width: open || inMobile ? 'auto' : 36,
            maxWidth: open || inMobile ? 180 : 36,
            objectFit: 'contain',
            objectPosition: 'left',
            transition: 'all 0.25s ease',
          }}
        />
      </Box>

      {/* User info */}
      {(open || inMobile) ? (
        <Box sx={{ p: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexDirection: 'row', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: '#00897b',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {user?.name?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>
              {user?.name}
            </Typography>
            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 160,  }}>
              {user?.role.toUpperCase()}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#00897b', fontSize: 13, fontWeight: 700 }}>
            {user?.name?.charAt(0)}
          </Avatar>
        </Box>
      )}

      {/* Menu items */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {MENU_ITEMS.map((item) => (
          <Tooltip
            key={item.path}
            title={!open && !inMobile ? item.label : ''}
            placement="right"
            arrow
          >
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (inMobile) setMobileOpen(false);
                }}
                selected={isActive(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open || inMobile ? 'initial' : 'center',
                  px: 2,
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(26,35,126,0.1)',
                    color: '#1a237e',
                    '& .MuiListItemIcon-root': { color: '#1a237e' },
                    '&:hover': { bgcolor: 'rgba(26,35,126,0.15)' },
                  },
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open || inMobile ? 2 : 'auto',
                    justifyContent: 'center',
                    color: isActive(item.path) ? '#1a237e' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(open || inMobile) && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: isActive(item.path) ? 600 : 400,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </Tooltip>
        ))}
      </List>

      <Divider />

      {/* Logout */}
      <Tooltip title={!open && !inMobile ? 'Logout' : ''} placement="right" arrow>
        <ListItem disablePadding>
          <ListItemButton
            onClick={logout}
            sx={{
              minHeight: 48,
              justifyContent: open || inMobile ? 'initial' : 'center',
              px: 2,
              mx: 1,
              mb: 1,
              mt: 0.5,
              borderRadius: 2,
              '&:hover': { bgcolor: 'rgba(198,40,40,0.08)', color: '#c62828' },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open || inMobile ? 2 : 'auto',
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {(open || inMobile) && (
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 14 }} />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer - 1,
          background: mode === 'dark' ? 'linear-gradient(90deg, #08e2c9ff 0%, #283593 30%, #1a237e 100%)' :'linear-gradient(90deg, #1a237e 0%, #283593 50%, #08e2c9ff 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          left: { md: `${drawerWidth}px` },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          transition: 'left 0.25s ease, width 0.25s ease',
          borderRadius: '0 !important'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1 }}
          >
            {open ? <MenuIcon /> : <MenuOpenIcon/> }
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 0.5 }}>
            POSP Training Portal
          </Typography>
          <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
            <IconButton color="inherit" onClick={toggleMode} size="small">
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Desktop Sidebar — permanent, highest z-index so it overlaps AppBar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            borderRadius: '0 !important',
            transition: 'width 0.25s ease',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              overflowX: 'hidden',
              transition: 'width 0.25s ease',
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '2px 0 12px rgba(0,0,0,0.06)',
              zIndex: (t) => t.zIndex.drawer + 2,
            },
          }}
        >
          {/* No <Toolbar /> spacer — logo IS the top section */}
          <DrawerContent />
        </Drawer>
      )}

      {/* Mobile Sidebar — temporary overlay, highest z-index */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_FULL,
              boxSizing: 'border-box',
              zIndex: (t) => t.zIndex.drawer + 10,
              top: 0,
            },
            '& .MuiBackdrop-root': {
              zIndex: (t) => t.zIndex.drawer + 9,
            },
          }}
        >
          <DrawerContent inMobile />
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          p: { xs: 2, sm: 3 },
          pt: { xs: 10, sm: 11 },
          minWidth: 0,
          transition: "margin 0.25s ease",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
        >
          Developed by{" "}
          <Link
            href="https://keelis.com/"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            color="inherit"
          >
            Keelis
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default AppLayout;
